import { useState } from "react";
import { DateTime } from "luxon";
import {
  PecCoverageDecisionSchema,
  pecCoverageDecisionLabels,
  type PecDocumentDetail,
  type PecCoverageDecision,
} from "cadendar-shared";
import { Button } from "../../../components/ui/button";
import { trpc } from "../../main/components/trpc";

export default function PecCoverageReview({
  document,
}: {
  document: PecDocumentDetail;
}) {
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState<{
    decision: PecCoverageDecision | null;
    expectedReviewCount: number;
  } | null>(null);
  const mutation = trpc.pec.reviewPecCoverage.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.pec.getPecDocument.invalidate(document.id),
        utils.pec.listPecDocuments.invalidate(),
      ]);
      setDraft(null);
    },
  });
  if (document.documentType !== "coverage_response" || !document.patientId)
    return null;
  const latest = document.coverageReviews.at(-1);
  return (
    <section
      aria-label="Décision de prise en charge"
      className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-4"
    >
      <h3 className="font-semibold text-slate-900">
        Décision de prise en charge
      </h3>
      <p className="mt-1 text-sm text-slate-700">
        {latest?.decision
          ? pecCoverageDecisionLabels[latest.decision]
          : "Décision non renseignée"}
      </p>
      <p className="mt-1 text-xs text-slate-500">
        À confirmer après lecture du document. L’OCR et les montants extraits ne
        valent pas accord.
      </p>
      {draft ? (
        <form
          className="mt-3 space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            mutation.mutate({
              id: document.id,
              decision: draft.decision,
              expectedReviewCount: draft.expectedReviewCount,
            });
          }}
        >
          <label className="block text-sm font-medium">
            Décision constatée dans le document
            <select
              className="mt-1 block w-full rounded border border-slate-300 bg-white p-2"
              value={draft.decision ?? ""}
              disabled={mutation.isPending}
              onChange={(event) => {
                const parsed = PecCoverageDecisionSchema.safeParse(
                  event.target.value,
                );
                setDraft({
                  ...draft,
                  decision: parsed.success ? parsed.data : null,
                });
              }}
            >
              <option value="">Non renseignée / à vérifier</option>
              {PecCoverageDecisionSchema.options.map((decision) => (
                <option key={decision} value={decision}>
                  {pecCoverageDecisionLabels[decision]}
                </option>
              ))}
            </select>
          </label>
          <div className="flex gap-2">
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? "Enregistrement…" : "Confirmer la décision"}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => setDraft(null)}
            >
              Annuler
            </Button>
          </div>
          {mutation.error ? (
            <div role="alert" className="space-y-2 text-sm text-red-700">
              <p>{mutation.error.message}</p>
              <button
                type="button"
                className="underline"
                onClick={async () => {
                  await Promise.all([
                    utils.pec.getPecDocument.invalidate(document.id),
                    utils.pec.listPecDocuments.invalidate(),
                  ]);
                  setDraft(null);
                  mutation.reset();
                }}
              >
                Recharger le document
              </button>
            </div>
          ) : null}
        </form>
      ) : (
        <Button
          type="button"
          variant="outline"
          className="mt-3"
          onClick={() => {
            mutation.reset();
            setDraft({
              decision: latest?.decision ?? null,
              expectedReviewCount: document.coverageReviews.length,
            });
          }}
        >
          {latest?.decision ? "Corriger la décision" : "Renseigner la décision"}
        </Button>
      )}
      {document.coverageReviews.length > 0 ? (
        <details className="mt-3 text-xs text-slate-600">
          <summary className="cursor-pointer">
            Historique des confirmations ({document.coverageReviews.length})
          </summary>
          <ol className="mt-2 space-y-2">
            {[...document.coverageReviews].reverse().map((review, index) => (
              <li key={`${review.reviewedAt}-${index}`}>
                {review.decision
                  ? pecCoverageDecisionLabels[review.decision]
                  : "Décision remise à vérifier"}
                {" · "}
                {DateTime.fromISO(review.reviewedAt).toFormat(
                  "dd/MM/yyyy HH:mm",
                )}
                {" · "}
                {review.reviewer}
              </li>
            ))}
          </ol>
        </details>
      ) : null}
    </section>
  );
}
